<template>
  <v-container
    fluid
    class="styleguide-page"
  >
    <v-row>
      <v-col cols="12">
        <h1>{{ t("Style Guide") }}</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">Headline 1</h1>
        <h2 class="text-h2">Headline 2</h2>
        <h3 class="text-h3">Headline 3</h3>
        <h4 class="text-h4">Headline 4</h4>
        <h5 class="text-h5">Headline 5</h5>
        <h6 class="text-h6">Headline 6</h6>
        <p>Paragraph</p>
      </v-col>
    </v-row>

    <!-- Buttons -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Buttons
        </h1>
      </v-col>

      <v-col cols="12">
        <Button color="primary">
          Button
        </Button>
      </v-col>

      <v-col cols="12">
        <Button color="error">
          Negative
        </Button>
      </v-col>

      <v-col cols="12">
        <Button>
          Next
          <v-icon
            end
            class="icon-flipped"
          >
            mdi-keyboard-backspace
          </v-icon>
        </Button>
      </v-col>

      <v-col cols="12">
        <Button color="secondary">
          Neutral
        </Button>
      </v-col>

      <v-col cols="12">
        <Button color="secondary text-red">
          Cancel order
        </Button>
      </v-col>

      <v-col cols="12">
        <v-menu
          location="bottom"
          :offset="[0, 4]"
        >
          <template #activator="{ props }">
            <Button
              v-bind="props"
              color="secondary"
            >
              <v-icon start>
                mdi-account-circle-outline
              </v-icon>
              Max Mustermann
              <v-icon end>
                mdi-chevron-down
              </v-icon>
            </Button>
          </template>
          <v-list>
            <v-list-item link>
              <v-list-item-title>Profile</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>

      <v-col cols="12">
        <v-menu
          location="bottom"
          :offset="[0, 4]"
        >
          <template #activator="{ props }">
            <Button
              v-bind="props"
              text
              x-large
              elevation="0"
            >
              <v-icon start>
                mdi-account-circle-outline
              </v-icon>
              Max Mustermann
              <v-icon end>
                mdi-chevron-down
              </v-icon>
            </Button>
          </template>
          <v-list>
            <v-list-item link>
              <v-list-item-title>Profile</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>

      <v-col cols="12">
        <Button color="grey-stroke text-black-75">
          Reset
        </Button>
      </v-col>

      <v-col cols="12">
        <Button
          color="white text-white"
          :icon-btn="true"
          large
          variant="outlined"
        >
          <v-icon>mdi-close</v-icon>
        </Button>
      </v-col>

      <v-col cols="12">
        <Button
          color="white text-primary"
          :icon-btn="true"
          :icon-with-background="true"
          small
        >
          <v-icon>mdi-eye-outline</v-icon>
        </Button>
      </v-col>
    </v-row>

    <!-- Input boxes -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Input boxes
        </h1>
      </v-col>

      <v-col cols="12">
        <InputText label="placeholder text" />
      </v-col>

      <v-col cols="12">
        <InputText label="Label" />
      </v-col>

      <v-col cols="12">
        <InputText
          label="Label"
          value="Filled"
        />
      </v-col>

      <v-col cols="12">
        <InputText
          label="Label"
          value="Filled"
          error
          error-messages="That email address doesn’t look correct."
        />
      </v-col>

      <v-col cols="12">
        <InputText
          label="Password"
          value="Filled"
          :type="'password'"
          error
        />
      </v-col>

      <v-col cols="12">
        <InputText
          label="Search..."
          :type="'search'"
          @click:append="runSearch"
        />
      </v-col>

      <v-col class="col">
        <InputText
          label="Textarea"
          multiline
        />
      </v-col>

      <v-col cols="12">
        <Dropdown
          label="Dropdown"
          :items="items"
          v-model="selectedItem"
        />
      </v-col>

      <v-col cols="12">
        <Dropdown
          label="Dropdown multiple"
          :items="items"
          multiple
          v-model="selectedItems"
        />
      </v-col>

      <v-col cols="12">
        <Combobox
          label="Combobox"
          :items="items"
        />
      </v-col>

      <v-col cols="12">
        <Combobox
          label="Combobox multiple"
          multiple
          :items="items"
        />
      </v-col>
    </v-row>

    <!-- Radio buttons -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">Radio buttons</h1>
      </v-col>
      <v-col>
        <v-radio-group v-model="radioGroup">
          <v-radio
            v-for="n in 2"
            :key="n"
            :label="`Radio ${n}`"
            :value="n"
          />
        </v-radio-group>
        <v-radio-group
          v-model="radioGroup"
          inline
        >
          <v-radio
            v-for="n in 2"
            :key="n"
            :label="`Radio ${n}`"
            :value="n"
          />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Checkboxes & Switches -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Checkboxes
        </h1>
      </v-col>

      <v-col cols="12">
        <v-checkbox
          v-for="n in 2"
          :key="n"
          v-model="checkbox[n - 1]"
          :value="n % 2"
          :label="`Checkbox ${n}`"
          hide-details
        />
      </v-col>

      <v-col cols="12">
        <v-switch
          v-for="n in 2"
          :key="n"
          v-model="switchModel[n - 1]"
          inset
          :label="`Switch ${n}`"
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Notification badge -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Notification
        </h1>
      </v-col>

      <v-col cols="12">
        <v-badge
          color="error"
          content="6"
          location="top end"
          offset-x="21"
          offset-y="21"
        >
          <Button
            color="primary"
            :icon-btn="true"
          >
            <v-icon>mdi-bell-outline</v-icon>
          </Button>
        </v-badge>
      </v-col>
    </v-row>

    <!-- Popup (Dialog) -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Popup
        </h1>
      </v-col>

      <v-col class="col">
        <v-dialog
          v-model="dialog"
        >
          <template #activator="{ props }">
            <Button v-bind="props">
              Open Dialog
            </Button>
          </template>
          <v-card>
            <v-card-title class="text-h3 text-center">
              Additional data for order ID123456
              <v-spacer />
              <Button
                :icon-btn="true"
                color="text-black"
                @click="dialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </Button>
            </v-card-title>
            <v-card-text>
              <InputText label="CeGaT ID" />
              <InputText label="Patient ID" />
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Table with pagination -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h1">
          Table
        </h1>
      </v-col>

      <v-col cols="12">
        <v-data-table
          v-model:sort-by="sortBy"
          :headers="headers"
          :items="patients"
          :no-data-text="t('No data available')"
          multi-sort
        />
        <v-pagination
          v-model="page"
          :length="pages"
          :total-visible="6"
          class="elevation-0"
          rounded
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import InputText from "@/components/input-components/InputText.vue";
import Button from "@/components/input-components/Button.vue";
import Combobox from "@/components/input-components/Combobox.vue";
import Dropdown from "@/components/input-components/Dropdown.vue";
import { Patient } from "@/entities/patient";

const { t } = useI18n();

defineOptions({ name: "StyleGuide" })

// Reactive state
const radioGroup = ref(2);
const checkbox = ref([false, true]);
const switchModel = ref([false, true]);
const page = ref(1);
const dialog = ref(false);
const showPw = ref(false);
const sortBy = ref([{ key: "firstName", order: "asc" }]);
const selectedItem = ref(null)
const selectedItems = ref([])

const items = ref(["Foo", "Bar", "Fizz", "Buzz"]);

// Table headers
const headers = ref([
  { title: "Patient name", key: "firstName" },
  { title: "Patient number", key: "number" },
  { title: "Patient d.o.b.", key: "dateOfBirth" },
  { title: "Sex", key: "sexId" },
  { title: "Actions", key: "actions" },
]);

// Generate dummy patients
const patients = computed<Patient[]>(() => {
  const result: Patient[] = [];
  for (let i = 0; i < 500; i++) {
    result.push({
      patientId: i,
      firstName: `Maximilius ${i + 1}`,
      lastName: `von Daner ${i + 1}`,
      number: `P123A45 ${i + 1}`,
      dateOfBirth: `12.03.1985 ${i + 1}`,
      sexId: "male",
      city: `City ${i + 1}`,
      street: `Street ${i + 1}`,
      country: `Country ${i + 1}`,
      patientNumber: `Patient ${i + 1}`,
    } as Patient);
  }
  return result;
});

// Pagination length
const pages = computed(() => Math.ceil(patients.value.length / 10));

// Methods
function runSearch() {
  alert("search");
}
</script>

<style scoped>
.styleguide-page {
  background-color: #e5e5e5;
}
.icon-flipped {
  transform: scaleX(-1);
}
</style>
